import { supabase } from '../lib/supabase';

/**
 * Ride Sharing Service
 * Handles all ride offer and passenger operations
 */

class RideService {
  /**
   * Get all rides for a specific event with driver info and passenger count
   * @param {string} eventId - Event UUID
   * @returns {Promise<Array>} Array of ride offers with driver details
   */
  async getRidesByEvent(eventId) {
    // Select ride details AND join with driver profile
    const { data, error } = await supabase?.from('ride_offers')?.select(`
        *,
        driver:driver_id (
          id,
          full_name,
          avatar_url
        ),
        passengers:ride_passengers (
          id,
          passenger_id,
          status,
          passenger_profile:passenger_id (
            id,
            full_name,
            avatar_url
          )
        )
      `)?.eq('event_id', eventId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching rides:', error);
      throw error;
    }
        
    return data;
  }

  /**
   * Create a new ride offer
   * @param {Object} rideData - Ride offer details
   * @returns {Promise<Object>} Created ride offer
   */
  async createRide(rideData) {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) {
        throw new Error('Araç ilanı oluşturmak için giriş yapmalısınız');
      }

      // Check if user already has a ride offer for this event
      const { data: existingRide, error: checkError } = await supabase?.from('ride_offers')?.select('id')?.eq('event_id', rideData?.eventId)?.eq('driver_id', user?.id)?.maybeSingle();

      if (checkError) throw checkError;

      if (existingRide) {
        throw new Error('Bu etkinlik için zaten bir araç ilanınız var');
      }

      // Create ride offer
      const { data: newRide, error: insertError } = await supabase?.from('ride_offers')?.insert({
          event_id: rideData?.eventId,
          driver_id: user?.id,
          origin_location: rideData?.originLocation,
          departure_time: rideData?.departureTime,
          available_seats: rideData?.availableSeats,
          price_per_seat: rideData?.pricePerSeat || 0,
          car_details: rideData?.carDetails || null,
          notes: rideData?.notes || null
        })?.select(`
          *,
          driver:driver_id (
            id,
            full_name,
            avatar_url
          )
        `)?.single();

      if (insertError) throw insertError;

      return newRide;
    } catch (error) {
      console.error('Error creating ride:', error);
      throw new Error(error?.message || 'Araç ilanı oluşturulurken hata oluştu');
    }
  }

  /**
   * Join a ride as a passenger
   * @param {string} rideId - Ride offer UUID
   * @returns {Promise<Object>} Created passenger record
   */
  async joinRide(rideId, eventId) {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      if (userError || !user) {
        throw new Error('Araca katılmak için giriş yapmalısınız');
      }

      // Get ride details to check availability
      const { data: ride, error: rideError } = await supabase?.from('ride_offers')?.select(`
          *,
          passengers:ride_passengers(id, status)
        `)?.eq('id', rideId)?.single();

      if (rideError) throw rideError;

      // Check if user is the driver
      if (ride?.driver_id === user?.id) {
        throw new Error('Kendi araç ilanınıza katılamazsınız');
      }

      // Check if user already joined this ride
      const { data: existingPassenger, error: checkError } = await supabase?.from('ride_passengers')?.select('id')?.eq('ride_id', rideId)?.eq('passenger_id', user?.id)?.maybeSingle();

      if (checkError) throw checkError;

      if (existingPassenger) {
        throw new Error('Bu araca zaten katıldınız');
      }

      // Check available seats
      const approvedPassengers = ride?.passengers?.filter(p => p?.status === 'approved')?.length || 0;
      if (approvedPassengers >= ride?.available_seats) {
        throw new Error('Bu araçta boş koltuk kalmamıştır');
      }

      // Join ride
      const { data: passenger, error: joinError } = await supabase?.from('ride_passengers')?.insert({
          ride_id: rideId,
          passenger_id: user?.id,
          status: 'approved'
        })?.select()?.single();

      if (joinError) throw joinError;

      // Send notification to driver
      try {
        await supabase?.from('notifications')?.insert({
          user_id: ride?.driver_id,
          title: 'Yeni Yolcu!',
          message: `Biri ${ride?.origin_location} kalkışlı aracına katıldı.`,
          type: 'info',
          action_url: `/events/${eventId}?tab=transport`
        });
      } catch (notifError) {
        console.error('Error sending notification:', notifError);
        // Don't throw - notification failure shouldn't block the join
      }

      return passenger;
    } catch (error) {
      console.error('Error joining ride:', error);
      throw new Error(error?.message || 'Araca katılırken hata oluştu');
    }
  }

  /**
   * Cancel a ride offer (Driver only)
   * @param {string} rideId - Ride offer UUID
   * @returns {Promise<void>}
   */
  async cancelRide(rideId) {
    try {
      // 1. Get Ride Details (Simple Select)
      const { data: ride, error: rideError } = await supabase?.from('ride_offers')?.select('event_id, origin_location')?.eq('id', rideId)?.single();

      if (rideError) {
        console.error('Error fetching ride details:', rideError);
        throw new Error('Araç bilgileri alınamadı');
      }

      if (!ride) {
        throw new Error('Araç ilanı bulunamadı');
      }

      // 2. Get ALL Passenger IDs (Simple Select from Join Table)
      const { data: passengers, error: passengersError } = await supabase?.from('ride_passengers')?.select('passenger_id')?.eq('ride_id', rideId);

      if (passengersError) {
        console.error('Error fetching passengers:', passengersError);
        // Don't throw - continue with deletion even if we can't notify
      }

      // 3. Notify Passengers (if any exist)
      if (passengers && passengers?.length > 0) {
        console.log(`Notifying ${passengers?.length} passengers about cancellation...`);
        
        const notifications = passengers?.map(p => ({
          user_id: p?.passenger_id,
          title: 'Yolculuk İptal Edildi ⚠️',
          message: `Sürücü, ${ride?.origin_location} kalkışlı araç paylaşımını iptal etti.`,
          type: 'warning',
          action_url: `/events/${ride?.event_id}?tab=transport`,
          read: false
        }));

        const { error: notifError } = await supabase?.from('notifications')?.insert(notifications);
        
        if (notifError) {
          console.error('Error sending cancellation notifications:', notifError);
          // Don't throw - notification failure shouldn't block cancellation
        } else {
          console.log('✅ Cancellation notifications sent successfully');
        }
      }

      // 4. Delete the ride (Constraint will cascade delete passengers)
      const { error: deleteError } = await supabase?.from('ride_offers')?.delete()?.eq('id', rideId);

      if (deleteError) {
        console.error('Error deleting ride:', deleteError);
        throw new Error('Araç ilanı silinirken hata oluştu');
      }

      console.log('✅ Ride cancelled successfully');
      return true;
    } catch (error) {
      console.error('Error in cancelRide:', error);
      throw error;
    }
  }

  /**
   * Leave a ride as a passenger
   * @param {string} rideId - Ride offer UUID
   * @param {string} eventId - Event UUID for notification link
   * @returns {Promise<void>}
   */
  async leaveRide(rideId, eventId) {
    try {
      const { data: { user: currentUser }, error: authError } = await supabase?.auth?.getUser();

      if (authError || !currentUser) {
        throw new Error('Araçtan ayrılmak için giriş yapmalısınız');
      }

      // 1. Get Ride Info (Need Driver ID) - Simple Select
      const { data: ride, error: rideError } = await supabase?.from('ride_offers')?.select('driver_id, origin_location')?.eq('id', rideId)?.single();

      if (rideError) {
        console.error('Error fetching ride details:', rideError);
        throw new Error('Araç bilgileri alınamadı');
      }

      if (!ride) {
        throw new Error('Araç ilanı bulunamadı');
      }

      // 2. Delete the passenger record
      const { error: deleteError } = await supabase?.from('ride_passengers')?.delete()?.eq('ride_id', rideId)?.eq('passenger_id', currentUser?.id);

      if (deleteError) {
        console.error('Error deleting passenger record:', deleteError);
        throw new Error('Araçtan ayrılırken hata oluştu');
      }

      // 3. Send Notification to Driver
      if (ride?.driver_id) {
        console.log('Sending leave notification to driver:', ride?.driver_id);
        
        const { error: notifError } = await supabase?.from('notifications')?.insert({
          user_id: ride?.driver_id,
          title: 'Yolcu Ayrıldı 👋',
          message: `Bir yolcu ${ride?.origin_location} kalkışlı aracından ayrıldı.`,
          type: 'info',
          action_url: `/events/${eventId}?tab=transport`,
          read: false
        });
        
        if (notifError) {
          console.error('Error sending leave notification:', notifError);
          // Don't throw - notification failure shouldn't block leave action
        } else {
          console.log('✅ Leave notification sent successfully');
        }
      }
      
      console.log('✅ Left ride successfully');
      return true;
    } catch (error) {
      console.error('Error in leaveRide:', error);
      throw error;
    }
  }
}

export const rideService = new RideService();